/**
 * @file   src\components\Onboarding\Dashboard\PropertyListing.js
 * @brief  This file is responsible for showing each want ad in want ad listing page
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ShareIcon from "../../../../images/share-icon.svg";
import LakeViewIcon from "../../../../images/lake-view-icon.svg";
import HouseTagIcon from "../../../../images/property-icon-house.svg";
import {
  loggedInUser,
  getLocalStorage,
  formateDate,
  durationForFlexibleDate,
  monthNameForFlexibleDate,
  getUserId,
  removeDecimalZeros,
  getUserType,
  getAddressDisplay,
  thousandSeperatedValue,
} from "../../../../helpers/common";
import { useSearchParams } from "react-router-dom";
import {
  ADTYPE_STR,
  IN_CITY_LIMITS,
  RURAL,
  USER_INFO_KEY,
  LOT_LAND,
  USER_TYPE_AGENT,
} from "../../../../constants/common";
import AddToClientWantAdModal from "../../../MemberWantAd/AddToClientWantAdModal";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { setLotSizeValue } from "../../../../helpers/createWantAd";
import { getWantAdById } from "../../../../services/wantAdService";
import DefaultProfile from "../../../../images/v2/Profiles.svg";
import AgentProfileCard from "../../../Common/AgentProfileCard";
import { getCombinedText } from "../../../../helpers/getPropertyIcons";
import ShareYourAdModal from "../../../MemberWantAd/v2/ShareYourAdModal";
import { useSelector } from "react-redux";
import PremiumPlanModal from "../../../PlanUpgrade/PremiumPlanModal";
import BasicPlanModal from "../../../../components/PlanUpgrade/BasicPlanModal";
import { formatThousandSeparatedValue } from "@/helpers";
import "@/styles/desktop/general.scss";

export const emptyPropertyPlaceholderImages = [
  'house-empty-placeholder-01.png',
  'house-empty-placeholder-02.png',
  'house-empty-placeholder-03.png',
  'house-empty-placeholder-04.png',
]

const PropertyListing = ({
  handleFavoriteOrUnfavoriteWantAd,
  onChangeText,
  onAddClient,
  handleInventoryClick,
  ...props
}) => {
  const [verifyUser, setVerifyUser] = useSearchParams();
  const isUserVerified = verifyUser.get("verify");
  const [isFavourite, setIsFavourite] = useState(0);
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const [modalShow, setModalShow] = useState(false); //
  const user = loggedInUser();
  const sessionData = getLocalStorage(USER_INFO_KEY);
  const {
    Bedrooms,
    score,
    Bathrooms,
    Sqft,
    n2h,
    isLotLand,
    LotType_Rural_City,
    Location_Img,
    photo,
    LotSz_mn,
    LotSz_mx,
    AdType,
    Inv_Id,
    wantAdId,
    Location,
    City,
    State,
    subPropertyTypeId,
    Date_From,
    AskingPrice_Rent,
    AskingPrice_Sell,
    Date_To,
    DateType,
    Duration,
    Month,
    Longitude,
    Latitude,
    mapRadius,
    userFirstName,
    userLastName,
    userAvatar,
    userCompany,
    Is_Agent_Created,
    slug,
    ZIP,
  } = props && props.list;

  const emptyPlaceholderImage = emptyPropertyPlaceholderImages[Math.floor(Math.random() * emptyPropertyPlaceholderImages.length)];
  const emptyPlaceholderImageUrl = require(`../../../../images/${emptyPlaceholderImage}`);

  const invPhoto = photo || emptyPlaceholderImageUrl;


  const [showShareModal, setShareModal] = useState(false);
  const handleClose = () => setShareModal(false);
  const [selectedWantAd, setSelectedWantAd] = useState({});
  const loggedInUserId = getUserId();
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );
  const [userDetails, setUserDetails] = useState(null);



  const price = AskingPrice_Rent || AskingPrice_Sell;

  const userType = getUserType();
  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);

  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      setUserDetails(userDetailsFromStore[0]);
    } else {
      setUserDetails(null);
    }
  }, [userDetailsFromStore]);

  const settings = {
    className: "slider variable-width",
    dots: false,
    infinite: false,
    centerMode: false,
    slidesToShow: 1,
    swipeToSlide: true,
    variableWidth: true,
    arrows: false,
  };

  /**
   * To display must haves in want ad tile
   * @returns
   */
  const loadMustHaves = () => {
    if (isLotLand) {
      if (LotType_Rural_City) {
        return (
          <div className="property-details">
            {LotType_Rural_City === 1 ? RURAL : IN_CITY_LIMITS}{" "}
            <span className="details-text">lot type</span>
            <span className="vertical-divider">|</span>
            {setLotSizeValue(
              removeDecimalZeros(LotSz_mn),
              removeDecimalZeros(LotSz_mx),
            )}{" "}
            {setLotSizeValue(
              removeDecimalZeros(LotSz_mn),
              removeDecimalZeros(LotSz_mx),
            ) === "Any" ? (
              <span className="details-text">lot size</span>
            ) : (
              <></>
            )}
          </div>
        );
      } else {
        return (
          <div className="property-details">
            {setLotSizeValue(
              removeDecimalZeros(LotSz_mn),
              removeDecimalZeros(LotSz_mx),
            )}{" "}
            {setLotSizeValue(
              removeDecimalZeros(LotSz_mn),
              removeDecimalZeros(LotSz_mx),
            ) === "Any" ? (
              <span className="details-text">lot size</span>
            ) : (
              <></>
            )}
          </div>
        );
      }
    } else {
      let bedRm;
      let bathRm;

      let timeLine;
      if (Bedrooms) {
        if (Bedrooms === 99) {
          bedRm = "Any";
        } else {
          bedRm = `${parseInt(Bedrooms)}+`;
        }
      }

      if (Bathrooms) {
        if (Bathrooms === 99) {
          bathRm = "Any";
        } else {
          bathRm = `${parseInt(Bathrooms)}+`;
        }
      }

      if (AdType === ADTYPE_STR) {
        if (DateType === 1 || DateType == null) {
          timeLine =
            formateDate(Date_From, "mm/dd/yyyy") +
            " - " +
            formateDate(Date_To, "mm/dd/yyyy");
        } else if (DateType === 2) {
          timeLine =
            durationForFlexibleDate(Duration) +
            " - " +
            monthNameForFlexibleDate(Month);
        }
      }

      return (
        <div className="property-details">
          {bedRm && (
            <>
              {bedRm} <span className="details-text">beds</span>
            </>
          )}
          {bathRm && (
            <>
              <span className="vertical-divider">|</span>
              {bathRm} <span className="details-text">baths</span>
            </>
          )}
          {Sqft ? <span className="vertical-divider">|</span> : ""}
          {Sqft && AdType !== ADTYPE_STR && (
            <>
              <span>{formatThousandSeparatedValue(Sqft)}</span>{" "}
              <span className="details-text"> sqft</span>
            </>
          )}
          {timeLine && AdType === ADTYPE_STR && (
            <>
              <span className="details-text">{timeLine}</span>
            </>
          )}
        </div>
      );
    }
  };

  /**
   * To get want ad details for share want ad
   * @param {*} wantAdId
   */
  const getWantAdDetails = async (wantAdId) => {
    if (wantAdId) {
      await getWantAdById(wantAdId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            setSelectedWantAd(responseResult);
            setShareModal(true);
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  /**
   * handle share want ad functionality
   * @param {*} e
   * @param {*} wantAdId
   */
  const handleShare = (e, wantAdId) => {
    e.stopPropagation();
    getWantAdDetails(wantAdId);
  };

  /**
   * To show property type icons on map image
   * @param {*} props
   * @returns
   */
  const renderTooltip = (props) => (
    <Tooltip
      id="button-tooltip"
      placement="bottom-start"
      className="custom-tooltip"
      {...props}
    >
      {subPropertyTypeId &&
        subPropertyTypeId?.length > 0 &&
        getCombinedText(subPropertyTypeId)}
    </Tooltip>
  );

  const loadNiceToHaveList = () => {
    return (
      n2h &&
      n2h.map((nh, index) => {
        const id = _.get(nh, "N2H_Id");
        const itemText = _.get(nh, "Text", "");
        const icon = _.get(nh, "Icon", "");
        return (
          <div
            className="inner-slider-wrap"
            key={index + "_" + _.get(nh, "Want_ad_Id", "")}
          >
            <div className="property-tag mb-0">
              <div className="tag-icon">
                <img
                  src={
                    icon ? require("../../../../images/" + icon) : LakeViewIcon
                  }
                />
              </div>
              <span>{itemText}</span>
            </div>
          </div>
        );
      })
    );
  };

  const { before, after } = getAddressDisplay(Location, City, State, ZIP);

  return (
    <>
      <a
        role="button"
        className="property-listing-link"
        href={void 0}
        onClick={() => handleInventoryClick(Inv_Id, slug)}
      >
        <div className="property-listing">
          <div className={`property-type-wrap`}>
            <OverlayTrigger
              placement="bottom-start"
              delay={{ show: 250, hide: 400 }}
              overlay={renderTooltip}
            >
              <div
                className={`property-tile-matches`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 100
                }}
              >
                {
                  <>
                    <img
                      src={HouseTagIcon}
                      alt={HouseTagIcon}
                      className="property-icon-img" style={{ width: "18px", height: "18px" }} />
                  </>
                }
                <p style={{ marginTop: 0, marginBottom: 0, fontSize: "12px", marginLeft: 5, fontWeight: 700 }}>For sale</p>
              </div>
            </OverlayTrigger>
          </div>

          <div className="property-img-wrap h-200">
            <img src={invPhoto} alt="Map Image" />
          </div>

          <div className="property-info-outer">
            <div className="property-top">
              <div className="property-info-wrap">
                <div className="price-container">
                  <div className="prize-range-badge">
                    ${thousandSeperatedValue(Math.ceil(price))}
                  </div>

                  <div className="match-tag">{score}% Match</div>
                </div>

                <h6 className="long-txt-fix">
                  {before}
                  {after ? (
                    <>
                      {" "}
                      <span className="dot-color long-txt-fix">
                        &bull;
                      </span>{" "}
                      <span className="long-txt-fix">{after}</span>
                    </>
                  ) : null}
                </h6>
                {loadMustHaves()}
              </div>

              <div
                className="social-btns-wrap rmt-12"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {user && (
                  <a
                    role="button"
                    className="social-content-btn mr-12"
                    href={void 0}
                    onClick={(e) => handleShare(e, wantAdId)}
                  >
                    <img src={ShareIcon} />
                  </a>
                )}
              </div>
            </div>
            <div className="property-btm">
              <div
                className="sliderwrapper"
                onClick={
                  n2h?.length > 0
                    ? (e) => {
                        e.stopPropagation();
                      }
                    : undefined
                }
              >
                {n2h?.length > 0 && (
                  <Slider {...settings}>{loadNiceToHaveList()}</Slider>
                )}
              </div>
              <div className="property-agent-info" style={{ marginLeft: 0 }}>
                {userType === USER_TYPE_AGENT ||
                  userType === 3 ||
                  userType === 2 ? (
                  <div className="property-agent-info">
                    <AgentProfileCard
                      isAgentCreated={Is_Agent_Created}
                      userAvatar={userAvatar}
                      userFirstName={userFirstName}
                      userLastName={userLastName}
                      userCompany={userCompany}
                      showAvatar={false}
                      showProBadge={false}
                      namePrefix="Listed by "
                      fallback={
                        <>
                          <div className="property-agent-profile-wrap">
                            <img
                              src={DefaultProfile}
                              alt="avatar"
                              className="property-agent-profile"
                              width={"35px"}
                              height={"35px"}
                            />
                          </div>
                          <div className="property-not-agent-name">Agent</div>
                        </>
                      }
                    />
                  </div>
                ) : (
                  <AgentProfileCard
                    isAgentCreated={Is_Agent_Created}
                    userAvatar={userAvatar}
                    userFirstName={userFirstName}
                    userLastName={userLastName}
                    userCompany={userCompany}
                    companySuffix=" -"
                    fallback={
                      <span className="property-not-agent-name">
                        {AdType === 1 ? "Buyer" : "Renter"}
                      </span>
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </a>
      {/*  Add to client favorite Modal */}
      <AddToClientWantAdModal
        show={modalShow}
        showAddToClientWantAdModal={() => setModalShow(true)}
        onHide={() => setModalShow(false)}
        data={props.results}
        handleSkip={() =>
          handleFavoriteOrUnfavoriteWantAd(isFavourite, wantAdId)
        }
        // handleConfirm={handleClientFavoriteWantAd}
        searchText={props.clientSearchText}
        onChangeText={(e) => onChangeText(e)}
        onAddClient={() => onAddClient()}
      />
      {/** Plan Modal */}
      {userDetails && userDetails?.userType == 1 ? (
        <PremiumPlanModal
          userDetails={userDetails}
          show={showPremiumPlanModal}
          onHide={PremiumPlanModalClose}
          showPlanModal={PremiumPlanModalShow}
        />
      ) : (
        <BasicPlanModal
          userDetails={userDetails}
          show={showPremiumPlanModal}
          onHide={PremiumPlanModalClose}
          showPlanModal={PremiumPlanModalShow}
        />
      )}
      {showShareModal && (
        <ShareYourAdModal
          show={showShareModal}
          onHide={handleClose}
          adDetails={selectedWantAd}
          isWantAdCreated={false}
          AdType={_.get(selectedWantAd, "adType", "") === "BUY" ? 1 : 2}
          isAdTypeIsLand={_.get(selectedWantAd, "prprtyType", "") === 6}
          adTypeBasedOnProperty={
            _.get(selectedWantAd, "prprtyType", "") === 6
              ? LOT_LAND
              : _.get(selectedWantAd, "adType", "")
          }
        />
      )}
    </>
  );
};
export default PropertyListing;

